import { useId, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import {
  WORKBOOK_MAX_SAMPLE_KEYS,
  type WorkbookSampling,
} from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { WorkbookEditor } from './useWorkbookEditor';
import { sourcePreviewQuery } from './useSourcePreview';
import {
  canAddStratum,
  hasStratum,
  samplingFor,
  samplingKeyChoices,
  samplingKeyId,
  stratumChoices,
  withStratum,
  type SamplingKeyChoice,
} from './workbookSamplingDraft';

type SamplingEditor = Pick<
  WorkbookEditor,
  'workbookId' | 'definition' | 'plan' | 'edits'
>;

/** A whole number typed into a field, within its bounds; the fallback while it isn't one. */
function wholeNumber(text: string, min: number, max: number, fallback: number) {
  const value = Number.parseInt(text, 10);
  return Number.isFinite(value)
    ? Math.min(max, Math.max(min, value))
    : fallback;
}

/**
 * The Workbook's Sampling: the key a Run on sample samples by (one of the
 * keys its lookups suggest), how many key values it takes, its seed, and its
 * strata. Opening it reads each data source's preview, so the lookups can be
 * traced to the columns they start from.
 */
export function WorkbookSamplingDialog({
  editor,
  open,
  onOpenChange,
  canUpdate,
}: {
  editor: SamplingEditor;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canUpdate: boolean;
}) {
  // The previews name each Source's columns; the Plan traces lookups with them.
  useQueries({
    queries: editor.definition.sources
      .filter((source) => source.kind !== 'input')
      .map((source) => ({
        ...sourcePreviewQuery(editor.workbookId, source),
        enabled: open,
      })),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {/* Mounted while open, so each opening starts from the saved Sampling. */}
        <SamplingForm
          editor={editor}
          canUpdate={canUpdate}
          close={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

type SetSampling = (sampling: WorkbookSampling) => void;

function SamplingForm({
  editor,
  canUpdate,
  close,
}: {
  editor: SamplingEditor;
  canUpdate: boolean;
  close: () => void;
}) {
  const { definition, plan } = editor;
  const choices = samplingKeyChoices(definition, plan);
  const [draft, setDraft] = useState<WorkbookSampling | undefined>(
    definition.sampling,
  );
  const [first] = choices;
  const chosen = draft ?? (first && samplingFor(first.keyColumns, undefined));
  const save = (sampling: WorkbookSampling | undefined) => {
    editor.edits.workbook.update((latest) => ({ ...latest, sampling }));
    close();
  };
  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="workbook sampling dialog title">Sampling</T>
        </DialogTitle>
        <DialogDescription>
          <T context="workbook sampling dialog description">
            Run on sample calculates the workbook over a few key values, such as
            item numbers, and every row that has one in every data source that
            has the key, so lookups still match. Its results are marked as a
            sample: they are never downloaded or acted on.
          </T>
        </DialogDescription>
      </DialogHeader>
      {chosen ? (
        <div className="flex flex-col gap-3 text-sm">
          <SamplingKeyField
            choices={choices}
            sampling={chosen}
            disabled={!canUpdate}
            setDraft={setDraft}
          />
          <SamplingCountFields
            sampling={chosen}
            disabled={!canUpdate}
            setDraft={setDraft}
          />
          <SamplingStrataFields
            choices={stratumChoices(definition, plan, chosen)}
            sampling={chosen}
            disabled={!canUpdate}
            setDraft={setDraft}
          />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          <T context="workbook sampling no key">
            No lookup links two data sources yet. A sampling key is a column,
            such as an item number, that the workbook looks up from one data
            source in another.
          </T>
        </p>
      )}
      <DialogFooter>
        {definition.sampling && canUpdate && (
          <Button
            type="button"
            variant="ghost"
            className="mr-auto"
            onClick={() => save(undefined)}
          >
            <T context="workbook sampling remove">Remove sampling</T>
          </Button>
        )}
        <Button type="button" variant="outline" onClick={close}>
          <T context="dialog cancel button">Cancel</T>
        </Button>
        <Button
          type="button"
          disabled={!canUpdate || !chosen}
          onClick={() => save(chosen)}
        >
          <T context="workbook sampling save">Save</T>
        </Button>
      </DialogFooter>
    </>
  );
}

/** Which key the sample is taken by: one the Workbook's lookups suggest. */
function SamplingKeyField({
  choices,
  sampling,
  disabled,
  setDraft,
}: {
  choices: readonly SamplingKeyChoice[];
  sampling: WorkbookSampling;
  disabled: boolean;
  setDraft: SetSampling;
}) {
  const gt = useGT();
  const id = useId();
  return (
    <label className="flex flex-col gap-1" htmlFor={id}>
      <T context="workbook sampling key">Sample by</T>
      <Select
        value={samplingKeyId(sampling.keyColumns)}
        disabled={disabled}
        onValueChange={(value) => {
          const choice = choices.find((item) => item.id === value);
          if (choice) setDraft(samplingFor(choice.keyColumns, sampling));
        }}
      >
        <SelectTrigger id={id} className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {choices.map((choice) => (
            <SelectItem key={choice.id} value={choice.id}>
              {gt('{key} in {sources}', {
                context: 'workbook sampling key choice',
                key: choice.name,
                sources: choice.sourceNames.join(', '),
              })}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}

/** How many key values, the minimum per stratum, and a new seed. */
function SamplingCountFields({
  sampling,
  disabled,
  setDraft,
}: {
  sampling: WorkbookSampling;
  disabled: boolean;
  setDraft: SetSampling;
}) {
  return (
    <div className="flex gap-2">
      <label className="flex flex-1 flex-col gap-1">
        <T context="workbook sampling size">Key values</T>
        <Input
          className="h-8"
          type="number"
          min={1}
          max={WORKBOOK_MAX_SAMPLE_KEYS}
          disabled={disabled}
          value={sampling.size}
          onChange={(event) =>
            setDraft({
              ...sampling,
              size: wholeNumber(
                event.target.value,
                1,
                WORKBOOK_MAX_SAMPLE_KEYS,
                sampling.size,
              ),
            })
          }
        />
      </label>
      <label className="flex flex-1 flex-col gap-1">
        <T context="workbook sampling minimum per stratum">
          At least per group
        </T>
        <Input
          className="h-8"
          type="number"
          min={0}
          max={100}
          disabled={disabled}
          value={sampling.minPerStratum}
          onChange={(event) =>
            setDraft({
              ...sampling,
              minPerStratum: wholeNumber(
                event.target.value,
                0,
                100,
                sampling.minPerStratum,
              ),
            })
          }
        />
      </label>
      <div className="flex flex-col gap-1">
        <T context="workbook sampling seed">Sample</T>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => setDraft({ ...sampling, seed: sampling.seed + 1 })}
        >
          <T context="workbook sampling new sample">New sample</T>
        </Button>
      </div>
    </div>
  );
}

/** The automatic strata switch, and the columns whose every value is a stratum. */
function SamplingStrataFields({
  choices,
  sampling,
  disabled,
  setDraft,
}: {
  choices: ReturnType<typeof stratumChoices>;
  sampling: WorkbookSampling;
  disabled: boolean;
  setDraft: SetSampling;
}) {
  return (
    <>
      <label className="flex items-center justify-between gap-2">
        <span>
          <T context="workbook sampling automatic strata">
            Always include edge cases: blanks, errors, the largest values and
            values a lookup misses
          </T>
        </span>
        <Switch
          checked={sampling.automaticStrata}
          disabled={disabled}
          onCheckedChange={(automaticStrata) =>
            setDraft({ ...sampling, automaticStrata })
          }
        />
      </label>
      {choices.length > 0 && (
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1">
            <T context="workbook sampling declared strata">
              Include every value of
            </T>
          </legend>
          <div className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-md border p-2">
            {choices.map((stratum) => {
              const checked = hasStratum(sampling, stratum);
              return (
                <label
                  key={`${stratum.sourceId}:${stratum.column}`}
                  className="flex items-center gap-2"
                >
                  <Checkbox
                    checked={checked}
                    disabled={
                      disabled || (!checked && !canAddStratum(sampling))
                    }
                    onCheckedChange={(next) =>
                      setDraft(withStratum(sampling, stratum, next === true))
                    }
                  />
                  {stratum.label}
                </label>
              );
            })}
          </div>
        </fieldset>
      )}
    </>
  );
}
